import * as THREE from "three";
import { useMemo } from "react";
import { ROOF_HEIGHT } from "../../lib/consts";
import { THICKNESS, Y, RAIL_HEIGHT, R_OUTER, R_INNER, hw, hl, filletStart } from "./shared";

export default function GlassCurtain() {
	const outerPoints = useMemo(() => {
		const corners: [number, number][] = [
			[-hw, 12], [-hw, -hl], [hw, -hl], [hw, 12],
			[8, 12], [8, 2], [-8, 2], [-8, 12],
		];
		const radii = [R_OUTER, R_OUTER, R_OUTER, R_OUTER, R_INNER, R_INNER, R_INNER, R_INNER];

		const aList: [number, number][] = [];
		const bList: [number, number][] = [];
		for (let i = 0; i < corners.length; i++) {
			const prev = corners.at((i - 1 + corners.length) % corners.length)!;
			const curr = corners[i];
			const next = corners[(i + 1) % corners.length];
			const { a, b } = filletStart(prev, curr, next, radii[i]);
			aList.push(a);
			bList.push(b);
		}

		const addSegment = (from: [number, number], to: [number, number], n: number, pts: [number, number][]) => {
			for (let j = 1; j < n; j++) {
				const t = j / n;
				pts.push([from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t]);
			}
		};

		const addCurve = (cp: [number, number], to: [number, number], n: number, pts: [number, number][]) => {
			const from = pts[pts.length - 1];
			for (let j = 1; j < n; j++) {
				const t = j / n;
				const mt = 1 - t;
				pts.push([mt * mt * from[0] + 2 * mt * t * cp[0] + t * t * to[0], mt * mt * from[1] + 2 * mt * t * cp[1] + t * t * to[1]]);
			}
		};

		const pts: [number, number][] = [];

		pts.push(aList[7]);
		addCurve(corners[7], bList[7], 8, pts);
		pts.push(bList[7]);
		for (const i of [0, 1, 2, 3]) {
			addSegment(bList[(i - 1 + corners.length) % corners.length], aList[i], 4, pts);
			pts.push(aList[i]);
			addCurve(corners[i], bList[i], 8, pts);
			pts.push(bList[i]);
		}
		addSegment(bList[3], aList[4], 4, pts);
		pts.push(aList[4]);
		addCurve(corners[4], bList[4], 8, pts);
		pts.push(bList[4]);
		return pts;
	}, []);

	const geo = useMemo(() => {
		const positions: number[] = [];
		const indices: number[] = [];
		const bottom = Y + THICKNESS + RAIL_HEIGHT;

		for (let i = 0; i < outerPoints.length - 1; i++) {
			const [x1, z1] = outerPoints[i];
			const [x2, z2] = outerPoints[i + 1];
			const base = positions.length / 3;
			positions.push(x1, bottom, z1);
			positions.push(x2, bottom, z2);
			positions.push(x1, ROOF_HEIGHT, z1);
			positions.push(x2, ROOF_HEIGHT, z2);
			indices.push(base, base + 1, base + 2);
			indices.push(base + 1, base + 3, base + 2);
		}

		const g = new THREE.BufferGeometry();
		g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
		g.setIndex(indices);
		g.computeVertexNormals();
		return g;
	}, [outerPoints]);

	return (
		<mesh geometry={geo} receiveShadow>
			<meshPhysicalMaterial
				color="#004d99"
				transparent
				opacity={0.25}
				roughness={0.05}
				metalness={0.1}
				side={THREE.DoubleSide}
			/>
		</mesh>
	);
}
